import useGetMyAccountPipelines from '@/api/queries/me/useGetMyAccountPipelines';
import { useSelector } from '@/api/state';
import { useTranslation } from '@/i18n';
import AutomationActionConfigFieldset from '@/ui/components/automationActionConfigFieldset/AutomationActionConfigFieldset';
import AutomationConditionValueField from '@/ui/components/automationConditionValueField/AutomationConditionValueField';
import { PermissionGuard } from '@/ui/components/permissionGuard/PermissionGuard';
import {
  CONDITION_OPERATOR_OPTIONS,
  FIELD_OPTIONS,
} from '@/ui/pages/automationsPageOld/automation.utils';
import {
  AutomationConfigBlock,
  AutomationConfigBlockType,
} from '@/ui/pages/automationsPageOld/AutomationConfigBlock';
import { CreateEditAutomationHeader } from '@/ui/pages/automationsPageOld/CreateEditAutomationHeader';
import AuthenticatedPageWrapper from '@/ui/pages/pageWrappers/authenticatedPageWrapper/AuthenticatedPageWrapper';
import BasicField from '@payaca/components/basicField/BasicField';
import Button from '@payaca/components/button/Button';
import {
  ButtonColourVariant,
  ButtonStyleVariant,
} from '@payaca/components/button/enums';
import CreateArea from '@payaca/components/clickableArea/CreateArea';
import DropdownField from '@payaca/components/dropdownField/DropdownField';
import {
  InputColourVariant,
  InputStyleVariant,
} from '@payaca/components/inputWrapper/InputWrapper';
import MiniLoader from '@payaca/components/miniLoader/MiniLoader';
import Modal from '@payaca/components/modal/Modal';
import ValidatedFieldWrapper from '@payaca/components/validatedFieldWrapper/ValidatedFieldWrapper';
import ValidatedForm from '@payaca/components/validatedForm/ValidatedForm';
import {
  automationActionTypeReadableNameMap,
  automationEntityActionTypeMap,
  automationEntityTypeReadableNameMap,
  automationEntityTypeSubscriptionMap,
  automationEntityTypeTriggerEventMap,
} from '@payaca/helpers/automationHelper';
import { getFieldValidatorsForCustomAutomation } from '@payaca/helpers/automationValidationHelper';
import { AutomationsPermissions } from '@payaca/permissions/automations/automations.permissions';
import { SubscriptionLevels } from '@payaca/permissions/default-role.config';
import {
  userHasRequiredPermission,
  userHasRequiredSubscription,
} from '@payaca/permissions/permissions.utils';
import * as automationActions from '@payaca/store/automations/automationActions';
import { AutomationDataAccessType } from '@payaca/types/automationDataAccessTypes';
import {
  Automation,
  AutomationAction,
  automationActionTypes,
  AutomationActionTypes,
  AutomationCondition,
  automationEntityTypes,
  AutomationEntityTypes,
} from '@payaca/types/automationTypes';
import { FieldValidationResult } from '@payaca/types/fieldValidationTypes';
import UntitledIcon from '@payaca/untitled-icons';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router';
import { Prompt, useLocation, useParams } from 'react-router-dom';
import { getUserRoles } from '../../../utils/stateAccessors';
import './CreateEditAutomation.sass';
import ActionIcon from './icons/action-icon.svg';

const newAutomationFormState: Partial<Automation<AutomationActionTypes>> = {
  id: 0,
  title: 'Untitled automation',
  entityType: 'Quote',
  triggerEvent: 'Created',
  conditions: [],
  actions: [],
  isActive: false,
};

const CreateEditAutomationPage = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const { automationId } = useParams<{ automationId: string }>();
  const searchParams = new URLSearchParams(location.search);
  const fromPipeline = searchParams.get('from') === 'pipeline';
  const pipelineId = searchParams.get('pipelineId');
  const [showAutomationActivationModal, setShowAutomationActivationModal] =
    useState(false);
  const [initialFormState, setInitialFormState] = useState<
    Partial<Automation<AutomationActionTypes>>
  >(newAutomationFormState);
  const [openSectionIndex, setOpenSectionIndex] = useState<number | null>(0);
  const [showAutomationResetModal, setShowAutomationResetModal] =
    useState(false);
  const [
    showInvalidSubmissionWarningModal,
    setShowInvalidSubmissionWarningModal,
  ] = useState(false);
  const [hasChangesMade, setHasChangesMade] = useState(false);
  const userRoles = useSelector(getUserRoles);

  const automation = useSelector(
    (state) =>
      state.automations.automations[
        automationId as unknown as number /* FIXME */
      ]?.entity
  );
  const accountSubscription = useSelector(
    (state) => state.subscription?.accountSubscription
  );
  const isPerformingAutomationAction = useSelector(
    (state) => state.automations.isPerformingAutomationAction
  );

  useEffect(() => {
    if (automation && !initialFormState.id) {
      setInitialFormState(automation);
    }
  }, [automation, initialFormState.id]);

  useEffect(() => {
    if (!automationId) return;
    dispatch(automationActions.requestGetAutomation(Number(automationId)));
  }, [dispatch, automationId]);

  const allowableEntityTypeOptions = useMemo(
    () =>
      automationEntityTypes
        .filter((option: AutomationEntityTypes) => {
          return (
            userHasRequiredSubscription(
              accountSubscription?.subscriptionProduct,
              automationEntityTypeSubscriptionMap[option] ||
                SubscriptionLevels.CORE
            ) && option != 'Invoice-v2'
          );
        })
        .map((value: AutomationEntityTypes) => ({
          label: automationEntityTypeReadableNameMap[value] || value,
          value: value,
        })),
    [accountSubscription]
  );

  const onToggleSection = useCallback(
    (newIndex: number) => {
      // are we closing the section we have open?
      if (openSectionIndex === newIndex) {
        return setOpenSectionIndex(null);
      }
      setOpenSectionIndex(newIndex);
    },
    [openSectionIndex]
  );

  const canUpdateTriggersAndConditions = useMemo(
    () =>
      userHasRequiredPermission(userRoles, [
        AutomationsPermissions.EDIT_AUTOMATION,
      ]),
    [userRoles]
  );

  const [formConditionsCount, setFormConditionsCount] = useState(0);
  const [formActionsCount, setFormActionsCount] = useState(0);

  const fieldValidators = useMemo(() => {
    return getFieldValidatorsForCustomAutomation({
      conditionsCount: formConditionsCount,
      actionsCount: formActionsCount,
    });
  }, [formConditionsCount, formActionsCount]);

  const onFormStateChange = useCallback(
    (newFormState: { [key: string]: any }) => {
      setHasChangesMade(true);
    },
    []
  );

  /* eslint-disable react-hooks/rules-of-hooks */
  const renderFormContents = useCallback(
    (
      isValid: boolean,
      formState: Automation<AutomationActionTypes>,
      validationState: {
        [key: string]: FieldValidationResult;
      },
      touchedState: {
        [key: string]: boolean;
      },
      onFieldChange: (value: { [key: string]: any }) => void,
      onFieldTouch: (fieldName: string) => void
    ) => {
      const triggerEventOptions = useMemo(
        () =>
          (formState?.entityType
            ? automationEntityTypeTriggerEventMap[formState.entityType] || []
            : []
          ).map((label: any) => ({ label, value: label })),
        // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useMemo has an unnecessary dependency: automation.
        // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useMemo has unnecessary dependency: automation
        [formState, automation]
      );

      const conditionFieldOptions = useMemo(() => {
        if (!formState.entityType) return [];

        const options = FIELD_OPTIONS.filter(
          (option: any) => !!option.entityColumns[formState?.entityType || '']
        )
          .filter((x) => {
            if (!formState.entityType) return false;

            const dataAccessType = x.entityColumns[formState.entityType];
            // filter out 'Newly-added tag' condition option if trigger event is not TAGGED
            if (dataAccessType === 'trigger.X.tagText') {
              return formState.triggerEvent === 'Tagged';
            }

            if (
              ['customEmailAddress', 'customTelephoneNumber'].includes(
                dataAccessType as AutomationDataAccessType
              )
            ) {
              return false;
            }

            // filter out 'New Pipeline stage' and 'Old Pipeline stage' condition options if trigger event is not related to pipeline stage changing
            if (
              dataAccessType === 'trigger.newPipelineStage' ||
              dataAccessType === 'trigger.oldPipelineStage'
            ) {
              return [
                'Pipeline stage changed',
                'Pipeline stage changed yesterday',
                'Pipeline stage changed 2 days ago',
                'Pipeline stage changed 3 days ago',
                'Pipeline stage changed 4 days ago',
                'Pipeline stage changed 5 days ago',
                'Pipeline stage changed 6 days ago',
                'Pipeline stage changed 7 days ago',
              ].includes(formState.triggerEvent);
            }

            return true;
          })
          .reduce((acc: { label: string; value: string }[], option) => {
            if (!formState.entityType) return acc;

            acc.push({
              label: option.label,
              value: option.entityColumns[formState.entityType] || '',
            });

            return acc;
          }, []);

        return options;
      }, [formState]);

      const onSaveAutomation = useCallback(
        (formStateToSave: any) => {
          setHasChangesMade(false);
          dispatch(
            automationActions.requestUpdateAutomation(
              Number(automationId),
              formStateToSave,
              () => {
                history.push(
                  fromPipeline && pipelineId
                    ? `/pipelines/${pipelineId}/config`
                    : '/automations?tab=custom'
                );
              }
            )
          );
        },
        // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useCallback has an unnecessary dependency: history.
        // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useCallback has an unnecessary dependency: automationId.
        // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useCallback has an unnecessary dependency: dispatch.
        // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useCallback has unnecessary dependency: formState
        [dispatch, history, automationId, formState]
      );

      const canEditTypeOrTriggerEvent = useMemo(
        () => !formState?.conditions?.length && !formState?.actions?.length,
        [formState]
      );

      const canAddConditions = useMemo(
        () => formState?.entityType && formState?.triggerEvent,
        [formState]
      );

      const canAddActions = useMemo(
        () => formState?.entityType && formState?.triggerEvent,
        [formState]
      );

      const onResetConfiguration = useCallback(() => {
        setInitialFormState({
          ...newAutomationFormState,
          id: formState.id,
          title: formState.title,
          isActive: formState.isActive,
        });
        setShowAutomationResetModal(false);
      }, [formState]);
      const onSubmitIncompleteAutomation = useCallback(() => {
        onSaveAutomation({ ...formState, isActive: false });
        // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useCallback has a missing dependency: 'onSaveAutomation'
      }, [formState]);

      // ensure the new entity we're selecting has a valid corresponding event type
      const onChangeEntityType = useCallback(
        (
          onFieldChange: any,
          changes: Partial<Automation<AutomationActionTypes>>
        ) => {
          const newTriggerEventOptions = changes?.entityType
            ? automationEntityTypeTriggerEventMap[changes.entityType] || []
            : [];
          if (
            formState?.triggerEvent &&
            !newTriggerEventOptions.includes(formState.triggerEvent)
          ) {
            return onFieldChange({
              ...changes,
              triggerEvent: newTriggerEventOptions[0],
            });
          }
          onFieldChange(changes);
        },
        // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useCallback has unnecessary dependency: triggerEventOptions
        [formState, triggerEventOptions]
      );

      const canBeSaved = useMemo(() => {
        if (
          formState?.title &&
          (formState?.title.length === 0 || formState?.title?.length > 80)
        ) {
          return false;
        }
        if (
          formState?.actions?.length &&
          formState?.actions?.find(
            (action: AutomationAction<AutomationActionTypes>) =>
              action?.title?.length > 40
          )
        ) {
          return false;
        }
        return true;
      }, [formState]);

      const { data: pipelinesData } = useGetMyAccountPipelines();

      const actionFieldOptions = useCallback(
        (formState: { [key: string]: any }) => {
          const pipelineIdString = formState.conditions.find(
            (condition: AutomationCondition) =>
              condition.field === 'deal.pipelineId' &&
              condition.operator === 'equals'
          )?.value;

          const hasV2PipelineCondition =
            pipelineIdString && pipelinesData
              ? !!pipelinesData.find((x) => x.id === pipelineIdString)
              : false;

          const actionTypes = formState?.entityType
            ? // @ts-expect-error
              automationEntityActionTypeMap[formState.entityType] || []
            : automationActionTypes;

          return actionTypes
            .filter((actionType: AutomationActionTypes) => {
              switch (actionType) {
                case 'Move Project to Pipeline stage':
                  return hasV2PipelineCondition;
                default:
                  return true;
              }
            })
            .map((value: AutomationActionTypes) => ({
              label: automationActionTypeReadableNameMap[value] || value,
              value: value,
            }));
        },
        [formState, pipelinesData]
      );
      return (
        <>
          <CreateEditAutomationHeader
            formState={formState}
            onFieldChange={onFieldChange}
          />
          <div className={'create-edit-automation-form-wrapper'}>
            <div className={'create-edit-automation-container'}>
              <div className={'create-edit-automation-controls'}>
                <div className={'create-edit-automation-background-line'} />
                <div className={'automation-config'}>
                  <PermissionGuard
                    renderIfHasPermissions={[
                      AutomationsPermissions.ADD_AUTOMATION,
                      AutomationsPermissions.ADD_SIMPLE_AUTOMATION,
                    ]}
                  >
                    <>
                      <AutomationConfigBlock
                        type={AutomationConfigBlockType.TRIGGER}
                      >
                        <div
                          className={'trigger-controls-wrapper'}
                          onClick={() =>
                            canUpdateTriggersAndConditions
                              ? !canEditTypeOrTriggerEvent
                                ? setShowAutomationResetModal(true)
                                : null
                              : null
                          }
                        >
                          <DropdownField
                            name={'entityType'}
                            label={'Automation type'}
                            styleVariant={InputStyleVariant.STANDARD}
                            colourVariant={InputColourVariant.SECONDARY}
                            value={formState?.entityType}
                            onChange={(changes: any) =>
                              onChangeEntityType(onFieldChange, changes)
                            }
                            onTouch={onFieldTouch}
                            options={allowableEntityTypeOptions}
                            isDisabled={
                              !canEditTypeOrTriggerEvent ||
                              !canUpdateTriggersAndConditions
                            }
                          />
                        </div>
                        <DropdownField
                          name={'triggerEvent'}
                          label={'What will be the trigger for your action(s)?'}
                          styleVariant={InputStyleVariant.STANDARD}
                          colourVariant={InputColourVariant.SECONDARY}
                          value={formState?.triggerEvent}
                          onChange={onFieldChange}
                          onTouch={onFieldTouch}
                          options={triggerEventOptions}
                          isDisabled={!canUpdateTriggersAndConditions}
                        />
                      </AutomationConfigBlock>
                      <AutomationConfigBlock
                        type={AutomationConfigBlockType.CONDITIONS}
                        title={
                          formState?.conditions?.length
                            ? `${formState.conditions.length} Condition(s)`
                            : 'Conditions'
                        }
                      >
                        {!canAddConditions && (
                          <div className={'condition-row'}>
                            Please configure your Trigger before adding any
                            Conditions.
                          </div>
                        )}
                        {canAddConditions && !formState?.conditions?.length && (
                          <div className={'conditions-empty-container'}>
                            <UntitledIcon
                              className="alert-icon"
                              name="info-circle.3"
                            />
                            <p>
                              <strong>{`You haven't added any Conditions - you can add them using the button below.`}</strong>
                            </p>
                            <p>{`If you don't add any Conditions, your Automation will run every time your Trigger is activated.`}</p>
                          </div>
                        )}
                        {formState?.conditions?.map(
                          (condition: any, i: number) => (
                            <div key={i} className={'condition-row'}>
                              <ValidatedFieldWrapper
                                validationResult={
                                  validationState[`conditions[${i}].field`]
                                }
                              >
                                <DropdownField
                                  name={`conditions[${i}].field`}
                                  styleVariant={InputStyleVariant.STANDARD}
                                  colourVariant={InputColourVariant.SECONDARY}
                                  value={condition.field}
                                  onChange={onFieldChange}
                                  options={conditionFieldOptions}
                                  isDisabled={!canUpdateTriggersAndConditions}
                                />
                              </ValidatedFieldWrapper>

                              <ValidatedFieldWrapper
                                validationResult={
                                  validationState[`conditions[${i}].operator`]
                                }
                              >
                                <DropdownField
                                  name={`conditions[${i}].operator`}
                                  styleVariant={InputStyleVariant.STANDARD}
                                  colourVariant={InputColourVariant.SECONDARY}
                                  value={condition.operator}
                                  onChange={onFieldChange}
                                  options={CONDITION_OPERATOR_OPTIONS.filter(
                                    (option: any) =>
                                      !option.allowableFieldValues ||
                                      option.allowableFieldValues.includes(
                                        condition.field
                                      )
                                  )}
                                  isDisabled={
                                    !condition.field ||
                                    !canUpdateTriggersAndConditions
                                  }
                                />
                              </ValidatedFieldWrapper>
                              <ValidatedFieldWrapper
                                validationResult={
                                  validationState[`conditions[${i}].value`]
                                }
                              >
                                <AutomationConditionValueField
                                  value={condition.value}
                                  field={condition.field}
                                  onConditionValueChange={(value) =>
                                    onFieldChange({
                                      [`conditions[${i}].value`]: value,
                                    })
                                  }
                                  automationTriggerEvent={
                                    formState.triggerEvent
                                  }
                                  isDisabled={!canUpdateTriggersAndConditions}
                                />
                              </ValidatedFieldWrapper>
                              {canUpdateTriggersAndConditions && (
                                <div className={'condition-delete'}>
                                  <Button
                                    colourVariant={ButtonColourVariant.WHITE}
                                    onClick={() => {
                                      const newConditions = [
                                        ...formState.conditions.filter(
                                          (c: any, conditionIndex: number) =>
                                            i !== conditionIndex
                                        ),
                                      ];
                                      onFieldChange({
                                        conditions: newConditions,
                                      });
                                      setFormConditionsCount(
                                        newConditions.length
                                      );
                                    }}
                                  >
                                    <UntitledIcon name="x.3" />
                                  </Button>
                                </div>
                              )}
                            </div>
                          )
                        )}
                        {canUpdateTriggersAndConditions && (
                          <CreateArea
                            isDisabled={!canAddConditions}
                            onClick={() => {
                              const newConditions = formState?.conditions
                                ? [
                                    ...formState.conditions,
                                    {
                                      field: '',
                                      operator: '',
                                      value: '',
                                    },
                                  ]
                                : [
                                    {
                                      field: '',
                                      operator: '',
                                      value: '',
                                    },
                                  ];
                              onFieldChange({
                                conditions: newConditions,
                              });
                              setFormConditionsCount(newConditions.length);
                            }}
                            className="add-new-condition"
                          >
                            Add condition
                          </CreateArea>
                        )}
                      </AutomationConfigBlock>
                    </>
                  </PermissionGuard>
                  {formState?.actions?.map(
                    (
                      action: AutomationAction<AutomationActionTypes>,
                      i: number
                    ) => {
                      return (
                        <AutomationConfigBlock
                          key={i}
                          type={AutomationConfigBlockType.ACTION}
                          title={formState?.actions?.[i]?.title}
                          stopTitleDescriptionPropagation={true}
                          additionalDescription={
                            <PermissionGuard
                              renderIfHasPermissions={[
                                AutomationsPermissions.ADD_AUTOMATION,
                                AutomationsPermissions.ADD_SIMPLE_AUTOMATION,
                              ]}
                            >
                              <ValidatedFieldWrapper
                                validationResult={
                                  validationState[`actions[${i}].type`]
                                }
                              >
                                <DropdownField
                                  colourVariant={InputColourVariant.WHITE}
                                  name={`actions[${i}].type`}
                                  value={action.type || undefined}
                                  options={actionFieldOptions(formState)}
                                  onChange={onFieldChange}
                                  onTouch={onFieldTouch}
                                />
                              </ValidatedFieldWrapper>
                            </PermissionGuard>
                          }
                          expandedTitle={
                            <div className="action-config-block-expanded-title">
                              <h2>{AutomationConfigBlockType.ACTION}</h2>
                              <ValidatedFieldWrapper
                                validationResult={
                                  validationState[`actions[${i}].title`]
                                }
                              >
                                <BasicField
                                  name={`actions[${i}].title`}
                                  value={formState?.actions?.[i]?.title}
                                  onChange={onFieldChange}
                                  styleVariant={InputStyleVariant.UNSTYLED}
                                />
                              </ValidatedFieldWrapper>
                            </div>
                          }
                        >
                          {/* TODO validation */}
                          <AutomationActionConfigFieldset
                            conditions={formState.conditions || []}
                            type={formState?.actions?.[i]?.type}
                            config={formState?.actions?.[i]?.config}
                            automationEntityType={formState.entityType}
                            validationState={Object.entries(
                              validationState
                            ).reduce(
                              (
                                acc: Record<string, FieldValidationResult>,
                                [key, value]
                              ) => {
                                if (key.startsWith(`actions[${i}].config.`)) {
                                  acc[
                                    key.replace(`actions[${i}].config.`, '')
                                  ] = value;
                                }
                                return acc;
                              },
                              {}
                            )}
                            onChange={(changeValue) => {
                              const mappedChangeValue: { [key: string]: any } =
                                {};
                              for (const [key, value] of Object.entries(
                                changeValue
                              )) {
                                mappedChangeValue[
                                  `actions[${i}].config.${key}`
                                ] = value;
                              }
                              onFieldChange(mappedChangeValue);
                            }}
                            isDefaultAutomation={false}
                            uploadIds={formState?.actions?.[i]?.uploadIds}
                            onUploadCollectionChange={(uploadIds) => {
                              onFieldChange({
                                [`actions[${i}].uploadIds`]: uploadIds,
                              });
                            }}
                            onTouch={(fieldName) => {
                              onFieldTouch(`actions[${i}].config.${fieldName}`);
                            }}
                            touchedState={
                              formState.isActive
                                ? Object.entries(touchedState).reduce(
                                    (
                                      acc: Record<string, boolean>,
                                      [key, value]
                                    ) => {
                                      if (
                                        key.startsWith(`actions[${i}].config.`)
                                      ) {
                                        acc[
                                          key.replace(
                                            `actions[${i}].config.`,
                                            ''
                                          )
                                        ] = value;
                                      }
                                      return acc;
                                    },
                                    {}
                                  )
                                : {}
                            }
                            showFieldValidationMessagesUnconditionally={true}
                          />
                          <PermissionGuard
                            renderIfHasPermissions={[
                              AutomationsPermissions.ADD_AUTOMATION,
                              AutomationsPermissions.ADD_SIMPLE_AUTOMATION,
                            ]}
                          >
                            <Button
                              className="delete-action"
                              colourVariant={ButtonColourVariant.SECONDARY}
                              styleVariant={ButtonStyleVariant.STANDARD}
                              isOutlined={true}
                              onClick={() => {
                                setOpenSectionIndex(null);
                                const newActions = formState?.actions
                                  ? [
                                      ...formState.actions.filter(
                                        (c: any, actionIndex: number) =>
                                          i !== actionIndex
                                      ),
                                    ]
                                  : [];
                                onFieldChange({
                                  actions: newActions,
                                });
                                setFormActionsCount(newActions.length);
                              }}
                            >
                              Delete action
                            </Button>
                          </PermissionGuard>
                        </AutomationConfigBlock>
                      );
                    }
                  )}
                  <PermissionGuard
                    renderIfHasPermissions={[
                      AutomationsPermissions.ADD_SIMPLE_AUTOMATION,
                      AutomationsPermissions.ADD_AUTOMATION,
                    ]}
                  >
                    <div>
                      <Button
                        className={'add-new-action'}
                        styleVariant={ButtonStyleVariant.OUTSIZE}
                        onClick={() => {
                          setOpenSectionIndex(formState?.actions?.length + 2);
                          const newActions = formState?.actions
                            ? [
                                ...formState.actions,
                                {
                                  title: 'Untitled Action',
                                  type: 'E-mail Notification',
                                  config: {},
                                },
                              ]
                            : [
                                {
                                  title: 'Untitled Action',
                                  type: 'E-mail Notification',
                                  config: {},
                                },
                              ];
                          onFieldChange({
                            actions: newActions,
                          });
                          setFormActionsCount(newActions.length);
                        }}
                        isDisabled={!canAddActions}
                      >
                        <img src={ActionIcon} />
                        Add action
                      </Button>
                    </div>
                  </PermissionGuard>
                </div>
              </div>
            </div>
            <Button
              styleVariant={ButtonStyleVariant.OUTSIZE}
              className="automation-save-button"
              type="button"
              onClick={() => {
                if (isValid && formState.isActive) {
                  onSaveAutomation(formState);
                } else if (!isValid && !formState.isActive) {
                  onSaveAutomation(formState);
                } else if (isValid && !formState.isActive) {
                  setShowAutomationActivationModal(true);
                } else {
                  setShowInvalidSubmissionWarningModal(true);
                }
              }}
              isDisabled={!canBeSaved || isPerformingAutomationAction}
              isProcessing={isPerformingAutomationAction}
            >
              Save
            </Button>
          </div>
          <Modal
            isOpen={showAutomationResetModal}
            onClose={() => setShowAutomationResetModal(false)}
            title={'This change will impact your current automation'}
            actions={
              <Button
                styleVariant={ButtonStyleVariant.OUTSIZE}
                onClick={onResetConfiguration}
              >
                Start again
              </Button>
            }
          >
            <p>{`You have Conditions/Actions based on the options you've selected in the Trigger section.`}</p>
            <p>{`We can remove these if you wish to start again (or perhaps create a new Automation).`}</p>
          </Modal>
          <Modal
            isOpen={showInvalidSubmissionWarningModal}
            onClose={() => setShowInvalidSubmissionWarningModal(false)}
            title={`We'll switch your automation "Off"`}
            actions={
              <Button
                styleVariant={ButtonStyleVariant.OUTSIZE}
                onClick={onSubmitIncompleteAutomation}
                isDisabled={isPerformingAutomationAction}
                isProcessing={isPerformingAutomationAction}
              >
                OK, no problem!
              </Button>
            }
          >
            <p>{`Your automation is switched "On" but your current configuration is not valid.`}</p>
            <p>{`Feel free to come back and finish off your setup later, then switch it back "On".`}</p>
          </Modal>
          <Prompt
            // only show prompt when there are changes made and hasnt finished updating or archiving
            when={hasChangesMade}
            message="There are unsaved changes on the page, are you sure you want to leave?"
          />
          <Modal
            isOpen={showAutomationActivationModal}
            onClose={() => setShowAutomationActivationModal(false)}
            actions={
              <>
                <Button
                  styleVariant={ButtonStyleVariant.OUTSIZE}
                  onClick={() => {
                    setShowAutomationActivationModal(false);
                    onSaveAutomation({ ...formState, isActive: true });
                  }}
                >
                  Switch on
                </Button>
                <Button
                  styleVariant={ButtonStyleVariant.ANCHOR}
                  onClick={() => {
                    setShowAutomationActivationModal(false);
                    onSaveAutomation(formState);
                  }}
                >
                  Continue
                </Button>
              </>
            }
          >
            <>
              <p style={{ textAlign: 'center' }}>
                This automation is currently switched <strong>OFF</strong> and
                will not run.
              </p>
            </>
          </Modal>
        </>
      );
    },
    // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useCallback has unnecessary dependency: onToggleSection
    // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useCallback has unnecessary dependency: openSectionIndex
    [
      automation,
      dispatch,
      history,
      automationId,
      openSectionIndex,
      allowableEntityTypeOptions,
      canUpdateTriggersAndConditions,
      isPerformingAutomationAction,
      showAutomationResetModal,
      showInvalidSubmissionWarningModal,
      hasChangesMade,
      showAutomationActivationModal,
      onToggleSection,
    ]
  );
  /* eslint-enable react-hooks/rules-of-hooks */

  const translate = useTranslation();

  return (
    <>
      <Helmet
        title={translate('pages.automation.title', {
          name: automation?.title || '',
        })}
      />
      <AuthenticatedPageWrapper
        breadcrumbItems={[
          {
            label: 'Back',
            to:
              fromPipeline && pipelineId
                ? `/pipelines/${pipelineId}/config`
                : `/automations?tab=custom`,
          },
          { label: 'Automation' },
        ]}
      >
        {!automation ? (
          <div className={'automation-loader'}>
            <MiniLoader />
          </div>
        ) : (
          <>
            <ValidatedForm<Partial<Automation<AutomationActionTypes>>>
              initialFormState={initialFormState}
              fieldValidators={fieldValidators}
              renderFormContents={renderFormContents as any}
              onFormStateChange={onFormStateChange}
            />
          </>
        )}
      </AuthenticatedPageWrapper>
    </>
  );
};

export default CreateEditAutomationPage;
